import {
  OnInit,
  OnDestroy,
  Component,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import {
  DataService,
  SharedModule,
  NotificationService,
  TypedBaseDetailComponent,
} from '@vendure/admin-ui/core';

import {
  Asset,
  Banner,
  BannerItem,
  CreateBannerDocument,
  CreateBannerInput,
  CreateBannerMutation,
  CreateBannerItemDocument,
  CreateBannerItemInput,
  Exact,
  GetBannerDocument,
  UpdateBannerDocument,
  UpdateBannerInput,
  UpdateBannerMutation,
  UpdateBannerItemDocument,
} from '../../../gql/graphql';
import { CustomValidators } from '../../../common/tools';
import { ImagePickerListComponent } from '../../common/image-picker-list/image-picker-list.component';
import { BannerItemListComponent } from '../banner-item-list/banner-item-list.component';

//TODO Remove item - bulkaction arquivo separado

@Component({
  selector: 'banner-detail.component',
  templateUrl: 'banner-detail.component.html',
  styleUrls: ['banner-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedModule, ImagePickerListComponent, BannerItemListComponent],
})
export class BannerDetailComponent
  extends TypedBaseDetailComponent<typeof GetBannerDocument, 'banner'>
  implements OnInit, OnDestroy
{
  item: BannerItem | undefined;
  asset: Asset[] = [];
  mobile: Asset[] = [];

  clear: EventEmitter<void> = new EventEmitter<void>();
  update: EventEmitter<void> = new EventEmitter<void>();

  itemsForm = this.formBuilder.group(
    {
      id: '',
      end: '',
      start: ['', Validators.required],
      link: ['', Validators.required],
      mobile: '',
      asset: ['', Validators.required],
    },
    { validators: [CustomValidators.EndAfterStartValidator('start', 'end')] },
  );

  detailForm = this.formBuilder.group({
    id: '',
    slug: ['', Validators.required],
  });

  onHttpRequest = false;

  constructor(
    protected dataService: DataService,
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef,
    private notificationService: NotificationService,
  ) {
    super();
  }

  setFormValues(entity: Banner): void {
    this.detailForm.patchValue({
      id: entity.id,
      slug: entity.slug,
    });
  }

  editItem(item: BannerItem) {
    console.log('EditItem', item);
    this.item = item;

    this.itemsForm.patchValue({
      id: this.item.id,
      link: this.item.link,
      start: this.item.start,
      end: this.item.end,
      asset: this.item.asset.id,
    });

    if (item.mobile) this.itemsForm.controls.mobile.setValue(item.mobile.id);

    this.asset = [item.asset];
    this.mobile = item.mobile ? [item.mobile] : [];

    this.itemsForm.markAsDirty();
    this.changeDetector.markForCheck();
  }

  clearItemForm() {
    this.item = undefined;

    this.itemsForm.patchValue({
      id: '',
      end: '',
      start: '',
      link: '',
    });

    this.asset = [];
    this.mobile = [];

    this.itemsForm.markAsPristine();
    this.changeDetector.markForCheck();

    this.clear.emit();
  }

  getObjectKeys(arg: Object) {
    if (!arg) return [];
    return Object.keys(arg);
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  ngOnInit(): void {
    this.init();
  }

  addItem() {
    if (this.hideSubmit(this.itemsForm)) return;

    this.onHttpRequest = true;
    const { id, asset, mobile, end, link, start } = this.itemsForm.value;

    if (!asset || !link || !start) return;

    let input: CreateBannerItemInput = {
      asset,
      link,
      mobile: mobile ? mobile : undefined,
      end: end ? end : undefined,
      start: start,
      banner: this.id,
    };

    if (id === '' || !id) {
      this.dataService.mutate(CreateBannerItemDocument, { input }).subscribe({
        next: () =>
          this.afterSuccess('grupo-loja.banner-item-create-success', true),
        error: this.afterFailure,
      });
    } else {
      this.dataService
        .mutate(UpdateBannerItemDocument, { input: { ...input, id } })
        .subscribe({
          next: () =>
            this.afterSuccess('grupo-loja.banner-item-update-success', true),
          error: this.afterFailure,
        });
    }
  }

  save() {
    if (this.hideSubmit(this.detailForm)) return;

    this.onHttpRequest = true;
    const { id, slug } = this.detailForm.value;

    if (!slug) return;
    const input: CreateBannerInput = {
      slug,
    };

    if (id === '' || !id) {
      this.dataService.mutate(CreateBannerDocument, { input }).subscribe({
        next: result => {
          this.afterSuccess('grupo-loja.banner-create-success');
          this.router.navigate(['../', result.createBanner.id], {
            relativeTo: this.route,
          });
        },
        error: this.afterFailure,
      });
    } else {
      this.dataService
        .mutate(UpdateBannerDocument, { input: { ...input, id } })
        .subscribe({
          next: () => this.afterSuccess('grupo-loja.banner-update-success'),
          error: this.afterFailure,
        });
    }
  }

  afterSuccess(message: string, clear: boolean = false) {
    this.onHttpRequest = false;
    if (clear) this.clearItemForm();

    this.notificationService.success(message);
    this.update.emit();
  }

  afterFailure(err: { message: string }) {
    this.onHttpRequest = false;
    this.notificationService.error(err.message);
  }

  hideSubmit(form: FormGroup<any>) {
    return form.invalid || form.pristine || this.onHttpRequest;
  }

  onAsset(assets: Asset[]) {
    this.itemsForm.controls.asset.setValue(
      assets.length > 0 ? assets[0].id : '',
    );
    this.itemsForm.controls.asset.markAsDirty();
    this.changeDetector.markForCheck();
  }

  onMobile(assets: Asset[]) {
    this.itemsForm.controls.mobile.setValue(
      assets.length > 0 ? assets[0].id : '',
    );
    this.itemsForm.controls.mobile.markAsDirty();
    this.changeDetector.markForCheck();
  }

  // //Awaiting this to be fixed https://github.com/vendure-ecommerce/vendure/issues/2535
  // canDeactivate(): boolean {
  //   console.log("canDeactivate");
  //   return super.canDeactivate();
  // }
}
